package com.siriusxm.pia.views.unifiedaggregator.management

import androidx.compose.runtime.*
import com.siriusxm.cmc.error
import com.siriusxm.pia.components.*
import com.siriusxm.pia.views.unifiedaggregator.AggregatorService
import com.siriusxm.pia.views.unifiedaggregator.ProducerDetail
import com.siriusxm.pia.views.unifiedaggregator.navigate
import unifiedcontent.aggregator.ProducerDetails
import unifiedcontent.aggregator.TypePermission
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

/**
 * A dedicated editor for managing which entity types a producer is allowed to
 * publish base/full updates for. Presents a scrollable checkbox list of all
 * known entity types. Checking or unchecking a type updates the local state;
 * changes are persisted only when the user clicks "Save".
 *
 * When saving, if any base types were removed compared to the original state,
 * a confirmation dialog is shown requiring the user to type "CONFIRM" before
 * proceeding, since removing base types could disrupt content flow.
 *
 * Uses dialogView to display Save/Cancel buttons at the bottom of the page.
 *
 * Navigated to from the producer detail page via the "Edit" button on the
 * Base Types box. Route: producers/{id}/base-types
 */
@Composable
fun BaseTypesEditor(aggregator: AggregatorService, producer: ProducerDetails) {
    val scope = rememberCoroutineScope()
    var typePermissions by remember {
        mutableStateOf(producer.typePermissions?.toList() ?: emptyList())
    }

    // Track the original base types for removal detection at save time
    val originalBaseTypes = remember {
        producer.typePermissions?.filter { it.base == true }?.map { it.type }?.toSet() ?: emptySet()
    }

    // State for the type removal confirmation dialog
    var showRemovalConfirmation by remember { mutableStateOf(false) }
    var removedTypeNames by remember { mutableStateOf(emptyList<String>()) }

    /**
     * Derives the set of entity type IDs where base publishing is currently
     * enabled from the local typePermissions state.
     */
    fun baseTypes(): Set<String> = typePermissions
        .filter { it.base == true }
        .map { it.type }
        .toSet()

    /**
     * Persists the current typePermissions to the server and navigates back
     * to the producer detail page.
     */
    suspend fun save() {
        try {
            aggregator.updateProducer(
                producer.copy(typePermissions = typePermissions)
            )
            aggregator.navigate(ProducerDetail(producer.id))
        } catch (e: Exception) {
            aggregator.notifications.error("Failed to save: ${e.message}")
        }
    }

    dialogView(producer.name ?: producer.id) {
        content {
            box({
                title = "Base Types"
                paddedContent = false
                header({
                    instruction = "Select the entity types this producer is allowed to publish base/full updates for."
                })
            }) {
                scrollableCheckBoxSelector(
                    items = aggregator.entityTypes,
                    selectedItems = baseTypes(),
                    itemLabel = { it.name },
                    itemIdentifier = { it.type },
                    onSelectionChange = { newBaseTypes ->
                        typePermissions = reconcileBaseTypes(typePermissions, newBaseTypes)
                    }
                )
            }

            // Render the type removal confirmation dialog when triggered by the save action
            if (showRemovalConfirmation) {
                TypeRemovalConfirmationDialog(
                    entityType = "producer",
                    removedTypeNames = removedTypeNames,
                    onConfirm = { confirmed ->
                        if (confirmed) {
                            scope.launch { save() }
                        }
                        showRemovalConfirmation = false
                    }
                )
            }
        }

        action {
            title = "Save"
            primary = true
            showProgressOnAction = true
            this.action {
                val currentBaseTypes = baseTypes()
                val removed = originalBaseTypes - currentBaseTypes

                if (removed.isNotEmpty()) {
                    removedTypeNames = removed.map { typeId ->
                        aggregator.entityType(typeId)?.name ?: typeId
                    }
                    showRemovalConfirmation = true
                } else {
                    save()
                }
            }
        }
        action("Cancel") {
            aggregator.navigate(ProducerDetail(producer.id))
        }
    }
}

/**
 * One row of a producer's partial permissions: a partial and the entity types it may be published
 * onto. When [allTypes] is true the partial is unrestricted (UCA-1162): it may be published onto every
 * entity type, including types added later, and [types] is empty.
 */
data class PartialAssignment(
    val partialId: String,
    val types: List<String>,
    val allTypes: Boolean
)

/**
 * Builds the partial rows for a producer from its restricted per-type permissions and its set of
 * unrestricted partials. An unrestricted partial is shown once as "All types", whatever per-type
 * entries also mention it. Rows are sorted by partial id.
 */
internal fun partialAssignments(
    typePermissions: List<TypePermission>,
    unrestrictedPartials: Set<String>
): List<PartialAssignment> {
    val partialToTypes = mutableMapOf<String, MutableList<String>>()
    typePermissions.forEach { perm ->
        perm.partials?.forEach { partialId ->
            if (partialId !in unrestrictedPartials) {
                partialToTypes.getOrPut(partialId) { mutableListOf() }.add(perm.type)
            }
        }
    }
    val restricted = partialToTypes.map { (partialId, types) -> PartialAssignment(partialId, types, false) }
    val unrestricted = unrestrictedPartials.map { PartialAssignment(it, emptyList(), true) }
    return (restricted + unrestricted).sortedBy { it.partialId }
}

/**
 * A dedicated editor for managing which partials a producer can submit, and
 * for which entity types each partial is allowed. Presents a table of
 * partial-to-types assignments with add, edit, and remove actions. Changes
 * are persisted only when the user clicks "Save".
 *
 * A partial can be granted for "All types" (UCA-1162), which the aggregator stores as an
 * unrestricted entry: the producer may publish it onto every entity type, including types added
 * later. The server lists such a partial under each of its schema's types in typePermissions, so
 * the editor keeps unrestricted partials in a separate set, strips them from its per-type state, and
 * always sends `unrestrictedPartials` on save. That way a save never turns an unrestricted grant
 * into one restricted to the schema's types.
 *
 * When saving, if any partials were removed compared to the original state,
 * a confirmation dialog is shown requiring the user to type "CONFIRM" before
 * proceeding, since removing partials could disrupt content flow.
 *
 * Uses dialogView to display Save/Cancel buttons at the bottom of the page.
 *
 * Navigated to from the producer detail page via the "Edit" button on the
 * Partials box. Route: producers/{id}/partials
 */
@Composable
fun PartialsEditor(aggregator: AggregatorService, producer: ProducerDetails) {
    val scope = rememberCoroutineScope()
    // Unrestricted ("All types") partials, kept apart from the restricted per-type permissions below.
    var unrestrictedPartials by remember {
        mutableStateOf(producer.unrestrictedPartials.orEmpty().toSet())
    }
    // Restricted per-type permissions only: unrestricted partials are stripped, since the server
    // expands them to their schema's types.
    var typePermissions by remember {
        mutableStateOf(
            unrestrictedPartials.fold(producer.typePermissions?.toList() ?: emptyList()) { perms, partialId ->
                removePartialFromAll(perms, partialId)
            }
        )
    }
    var showPartialEditor by remember { mutableStateOf(false) }
    var editingPartial by remember { mutableStateOf<PartialAssignment?>(null) }

    /**
     * Returns the set of partial IDs currently granted, restricted or unrestricted.
     */
    fun currentPartials(): Set<String> =
        typePermissions.flatMap { it.partials.orEmpty() }.toSet() + unrestrictedPartials

    // Track the original partials for removal detection at save time
    val originalPartials = remember { currentPartials() }

    // State for the partial removal confirmation dialog
    var showRemovalConfirmation by remember { mutableStateOf(false) }
    var removedPartialNames by remember { mutableStateOf(emptyList<String>()) }

    /**
     * Persists the current permissions to the server and navigates back to the producer detail
     * page. unrestrictedPartials is always sent, even when empty, because the server treats it as
     * the complete unrestricted set; omitting it would keep grants the user removed or narrowed.
     */
    suspend fun save() {
        try {
            aggregator.updateProducer(
                producer.copy(
                    typePermissions = typePermissions,
                    unrestrictedPartials = unrestrictedPartials.sorted()
                )
            )
            aggregator.navigate(ProducerDetail(producer.id))
        } catch (e: Exception) {
            aggregator.notifications.error("Failed to save: ${e.message}")
        }
    }

    dialogView(producer.name ?: producer.id) {
        content {
            box({
                paddedContent = false
                header({
                    instruction = "Partials this producer is allowed to submit, and for which entity types."
                })
                action {
                    title = "Add Partial"
                    primary = false
                    this.action {
                        editingPartial = null
                        showPartialEditor = true
                    }
                }
            }) {
                val assignments = partialAssignments(typePermissions, unrestrictedPartials)
                if (assignments.isEmpty()) {
                    boxMessage("No partial permissions configured. Click 'Add Partial' to get started.")
                } else {
                    table<PartialAssignment> {
                        items(assignments)

                        column("Partial") {
                            content { assignment ->
                                Text(aggregator.partial(assignment.partialId)?.name ?: assignment.partialId)
                            }
                        }

                        column("Types") {
                            content { assignment ->
                                Div {
                                    if (assignment.allTypes) {
                                        Text(ALL_TYPES_LABEL)
                                    } else {
                                        assignment.types.forEachIndexed { index, typeId ->
                                            if (index > 0) Text(", ")
                                            Text(aggregator.entityType(typeId)?.name ?: typeId)
                                        }
                                    }
                                }
                            }
                        }

                        column {
                            width = 60.px
                            content { assignment ->
                                Div({
                                    style {
                                        display(DisplayStyle.Flex)
                                        gap(8.px)
                                        justifyContent(JustifyContent.FlexEnd)
                                    }
                                }) {
                                    svgIcon(SVGIcons.pencil) {
                                        size = 16.px
                                        title = "Edit"
                                        action {
                                            editingPartial = assignment
                                            showPartialEditor = true
                                        }
                                    }
                                    svgIcon(SVGIcons.trash) {
                                        size = 16.px
                                        title = "Remove"
                                        action {
                                            typePermissions = removePartialFromAll(typePermissions, assignment.partialId)
                                            unrestrictedPartials = unrestrictedPartials - assignment.partialId
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }

            if (showPartialEditor) {
                PartialPermissionEditorDialog(
                    aggregator = aggregator,
                    assignedPartials = currentPartials(),
                    existingPartial = editingPartial,
                    onSave = { partialId, selectedTypes, allTypes ->
                        if (allTypes) {
                            typePermissions = removePartialFromAll(typePermissions, partialId)
                            unrestrictedPartials = unrestrictedPartials + partialId
                        } else {
                            typePermissions = setPartialOnTypes(typePermissions, partialId, selectedTypes)
                            unrestrictedPartials = unrestrictedPartials - partialId
                        }
                        showPartialEditor = false
                        editingPartial = null
                    },
                    onCancel = {
                        showPartialEditor = false
                        editingPartial = null
                    }
                )
            }

            // Render the partial removal confirmation dialog when triggered by the save action
            if (showRemovalConfirmation) {
                TypeRemovalConfirmationDialog(
                    entityType = "producer",
                    removedTypeNames = removedPartialNames,
                    onConfirm = { confirmed ->
                        if (confirmed) {
                            scope.launch { save() }
                        }
                        showRemovalConfirmation = false
                    }
                )
            }
        }

        action {
            title = "Save"
            primary = true
            showProgressOnAction = true
            this.action {
                val removed = originalPartials - currentPartials()

                if (removed.isNotEmpty()) {
                    removedPartialNames = removed.map { partialId ->
                        aggregator.partial(partialId)?.name ?: partialId
                    }
                    showRemovalConfirmation = true
                } else {
                    save()
                }
            }
        }
        action("Cancel") {
            aggregator.navigate(ProducerDetail(producer.id))
        }
    }
}

/** How an unrestricted partial grant reads wherever the console lists a producer's partials. */
internal const val ALL_TYPES_LABEL = "All types"

/**
 * A dedicated editor for managing which editable relationship types a producer
 * is permitted to create and delete. Presents a scrollable checkbox list of all
 * editable relationship types. Checking or unchecking a relationship updates
 * the local state; changes are persisted only when the user clicks "Save".
 *
 * Uses dialogView to display Save/Cancel buttons at the bottom of the page.
 *
 * Navigated to from the producer detail page via the "Edit" button on the
 * Relationship Permissions box. Route: producers/{id}/relationship-permissions
 */
@Composable
fun RelationshipPermissionsEditor(aggregator: AggregatorService, producer: ProducerDetails) {
    val scope = rememberCoroutineScope()
    var allowedRelationships by remember {
        mutableStateOf(producer.allowedRelationships?.toSet() ?: emptySet())
    }

    /**
     * Persists the current allowedRelationships to the server and navigates back
     * to the producer detail page.
     */
    suspend fun save() {
        try {
            aggregator.updateProducer(
                producer.copy(allowedRelationships = allowedRelationships.toList())
            )
            aggregator.navigate(ProducerDetail(producer.id))
        } catch (e: Exception) {
            aggregator.notifications.error("Failed to save: ${e.message}")
        }
    }

    // Filter to only editable relationships
    val editableRelationships = aggregator.relationships.filter { it.editable }

    dialogView(producer.name ?: producer.id) {
        content {
            box({
                title = "Relationships"
                paddedContent = false
                header({
                    instruction = "Select the editable relationship types this producer can create and delete."
                })
            }) {
                if (editableRelationships.isEmpty()) {
                    boxMessage("No editable relationship types are available.")
                } else {
                    scrollableCheckBoxSelector(
                        items = editableRelationships,
                        selectedItems = allowedRelationships,
                        itemLabel = { it.name },
                        itemIdentifier = { it.id },
                        onSelectionChange = { newRelationships ->
                            allowedRelationships = newRelationships
                        }
                    )
                }
            }
        }

        action {
            title = "Save"
            primary = true
            showProgressOnAction = true
            this.action {
                save()
            }
        }
        action("Cancel") {
            aggregator.navigate(ProducerDetail(producer.id))
        }
    }
}

/**
 * Updates the typePermissions list to reflect a new set of base types.
 * Types newly added get a TypePermission with base=true. Types removed
 * get base set to false. Types with only base=false and no partials
 * are pruned from the list.
 */
internal fun reconcileBaseTypes(
    current: List<TypePermission>,
    newBaseTypes: Set<String>
): List<TypePermission> {
    val updated = current.map { perm ->
        TypePermission(type = perm.type, base = perm.type in newBaseTypes, partials = perm.partials)
    }

    val existingTypes = updated.map { it.type }.toSet()
    val added = newBaseTypes
        .filter { it !in existingTypes }
        .map { type -> TypePermission(type = type, base = true, partials = emptyList()) }

    return (updated + added).filter { it.base == true || !it.partials.isNullOrEmpty() }
}

/**
 * Removes a partial from all TypePermission entries. Prunes entries
 * that end up with base=false and no partials.
 */
internal fun removePartialFromAll(
    current: List<TypePermission>,
    partialId: String
): List<TypePermission> {
    return current.map { perm ->
        TypePermission(
            type = perm.type,
            base = perm.base,
            partials = perm.partials?.filter { it != partialId }
        )
    }.filter { it.base == true || !it.partials.isNullOrEmpty() }
}

/**
 * Sets a partial on the specified types, removing it from any types
 * not in the set. Creates new TypePermission entries as needed.
 * Prunes entries that end up with base=false and no partials.
 */
internal fun setPartialOnTypes(
    current: List<TypePermission>,
    partialId: String,
    types: Set<String>
): List<TypePermission> {
    val updated = current.map { perm ->
        val hasPartial = perm.partials?.contains(partialId) == true
        val shouldHave = perm.type in types
        when {
            shouldHave && !hasPartial -> TypePermission(
                type = perm.type, base = perm.base,
                partials = perm.partials.orEmpty() + partialId
            )
            !shouldHave && hasPartial -> TypePermission(
                type = perm.type, base = perm.base,
                partials = perm.partials?.filter { it != partialId }
            )
            else -> perm
        }
    }

    val existingTypes = updated.map { it.type }.toSet()
    val added = types
        .filter { it !in existingTypes }
        .map { type -> TypePermission(type = type, base = false, partials = listOf(partialId)) }

    return (updated + added).filter { it.base == true || !it.partials.isNullOrEmpty() }
}

/**
 * Dialog for adding or editing a partial permission. Shows a partial
 * selector (when adding), an "All types" checkbox, and a list of entity types
 * that partial can be applied to. Only types that the partial schema declares
 * as applicable are shown. Used by PartialsEditor.
 *
 * "All types" grants the partial for every entity type, including types added
 * later and types its schema does not declare (UCA-1162). While it is on the
 * type list is hidden, since it no longer applies; turning it off pre-ticks the
 * schema's types so narrowing starts from what the schema covers.
 *
 * @param assignedPartials Partials the producer already has, hidden from the selector when adding.
 * @param existingPartial The row being edited, or null when adding.
 * @param onSave Called with the partial, its ticked types, and whether "All types" is on.
 */
@Composable
fun PartialPermissionEditorDialog(
    aggregator: AggregatorService,
    assignedPartials: Set<String>,
    existingPartial: PartialAssignment?,
    onSave: (partialId: String, types: Set<String>, allTypes: Boolean) -> Unit,
    onCancel: () -> Unit
) {
    val isEditing = existingPartial != null
    var selectedPartialId by remember { mutableStateOf(existingPartial?.partialId) }
    var selectedTypes by remember {
        mutableStateOf(existingPartial?.types?.toSet() ?: emptySet<String>())
    }
    var allTypes by remember { mutableStateOf(existingPartial?.allTypes == true) }

    // Available partials for the dropdown — exclude already-assigned when adding
    val availablePartials = if (isEditing) {
        aggregator.partials
    } else {
        aggregator.partials.filter { it.id !in assignedPartials }
    }

    // Entity types that the selected partial applies to (from the partial schema)
    val applicableTypes = remember(selectedPartialId) {
        if (selectedPartialId == null) emptyList()
        else {
            val partial = aggregator.partials.find { it.id == selectedPartialId }
            partial?.types?.mapNotNull { schemaUrl ->
                val typeId = schemaUrl.substringAfterLast("/", "").substringBefore(":")
                aggregator.entityType(typeId)?.let { typeId to it }
            }.orEmpty().sortedBy { (_, config) -> config.name.lowercase() }
        }
    }

    dialogOverlay {
        title = if (isEditing) "Edit Partial Permission" else "Add Partial Permission"

        onClose {
            onCancel()
        }

        content {
            Div({
                style {
                    padding(16.px)
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(16.px)
                }
            }) {
                dialogField("Partial", "The partial to configure.") {
                    if (isEditing) {
                        Text(aggregator.partial(selectedPartialId!!)?.name ?: selectedPartialId!!)
                    } else {
                        simpleSelectNullable(
                            selectedPartialId?.let { id -> availablePartials.find { it.id == id } },
                            availablePartials.map { (it.name ?: it.id) to it },
                            noneLabel = "Select a partial"
                        ) { selected ->
                            selectedPartialId = selected?.id
                            selectedTypes = emptySet()
                            allTypes = false
                        }
                    }
                }

                if (selectedPartialId != null) {
                    dialogField(
                        "Scope",
                        "Allow this partial on every entity type, including types added later."
                    ) {
                        simpleCheckBox(ALL_TYPES_LABEL, allTypes) { checked ->
                            allTypes = checked
                            if (!checked && selectedTypes.isEmpty()) {
                                selectedTypes = applicableTypes.map { (typeId, _) -> typeId }.toSet()
                            }
                        }
                    }

                    // The type list does not apply to an unrestricted grant, so it is hidden.
                    if (!allTypes) {
                        if (applicableTypes.isNotEmpty()) {
                            dialogField("Types", "Select which entity types this partial can be applied to for this producer.") {
                                scrollableCheckBoxSelector(
                                    items = applicableTypes,
                                    selectedItems = selectedTypes,
                                    itemLabel = { (_, config) -> config.name },
                                    itemIdentifier = { (typeId, _) -> typeId },
                                    onSelectionChange = { newTypes ->
                                        selectedTypes = newTypes
                                    }
                                )
                            }
                        } else {
                            dialogField("Types") {
                                Text("No entity types are defined for this partial.")
                            }
                        }
                    }
                }
            }
        }

        action {
            title = if (isEditing) "Save" else "Add"
            primary = true
            enabled = selectedPartialId != null && (allTypes || selectedTypes.isNotEmpty())
            this.action {
                onSave(selectedPartialId!!, if (allTypes) emptySet() else selectedTypes, allTypes)
            }
        }
    }
}
